import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { httpService } from '../services/httpService';
import { config } from '../services/configService';
import { eventBus } from '../eventBus';
import { log } from '../utils/DebugLogger';

interface TranslationsServiceResponse<T = unknown> {
  success: boolean;
  dados?: T;
  data?: T;
  error?: string;
}

export function useLanguagesAdmin() {
  const { t } = useI18n();
  const rows = ref<any[]>([]);
  const loading = ref(false);
  const error = ref(null);

  const baseUrl = config.api.idiomasHost;

  const fetchAll = async () => {
    log('useLanguagesAdmin', 'fetchAll', 'Fetching languages');
    loading.value = true;
    error.value = null;

    try {
      // External Go translations-service — no PHP contract applies
      const res = await httpService.get<TranslationsServiceResponse<any[]>>(`${baseUrl}/languages/list`);
      if (!res.success) {
        throw new Error(res.error || 'Failed to fetch languages');
      }
      rows.value = res.dados || res.data || [];
      return rows.value;
    } catch (err) {
      log('useLanguagesAdmin', 'fetchAll', 'Error', err);
      error.value = err.message;
      eventBus.emit('toast.show', { severity: 'error', summary: t('Erro'), detail: err.message });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const create = async ({ id, name, description }: { id: string; name: string; description?: string }) => {
    log('useLanguagesAdmin', 'create', 'Creating language', { id, name });
    loading.value = true;
    error.value = null;

    try {
      // External Go translations-service — no PHP contract applies
      const res = await httpService.post<TranslationsServiceResponse<{ id: string; name: string }>>(`${baseUrl}/languages/create`, {
        id,
        name,
        description: description || ''
      });

      if (!res.success) {
        throw new Error(res.error || 'Failed to create language');
      }

      eventBus.emit('toast.show', {
        severity: 'success',
        summary: t('Sucesso'),
        detail: t('Idioma adicionado')
      });

      await fetchAll();
      return res;
    } catch (err) {
      log('useLanguagesAdmin', 'create', 'Error', err);
      error.value = err.message;
      eventBus.emit('toast.show', { severity: 'error', summary: t('Erro'), detail: err.message });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    rows,
    loading,
    error,
    fetchAll,
    create
  };
}
